import { Module } from 'vuex';
import { RootState, CaseNotePaneState, CaseNotePaneKey } from '../types';

const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';
const SET_EDITING_MODE = 'SET_EDITING_MODE';
const SET_LAST_ERROR = 'SET_LAST_ERROR';
const TOGGLE_PANE = 'TOGGLE_PANE';
const SET_PROFILE_ID = 'SET_PROFILE_ID';
const SET_NOTE_ID = 'SET_NOTE_ID';
const SET_CAN_EDIT_NOTE = 'SET_CAN_EDIT_NOTE';
const SET_IS_PANE_OPEN = 'SET_IS_PANE_OPEN';
const SET_PANE_CLOSE_TIMEOUT = 'SET_PANE_CLOSE_TIMEOUT';

export const CaseNotePaneActionKeys = {
	openPane: 'openPane',
	closePane: 'closePane',
	setEditing: 'setEditing',
	unsetEditing: 'unsetEditing',
};

export const CaseNotePaneGetterKeys = {
	pane: 'pane',
	isPaneOpen: 'isPaneOpen',
	loading: 'loading',
	canEditNote: 'canEditNote',
	isEditing: 'isEditing',
	lastError: 'lastError',
	profileId: 'profileId',
	noteId: 'noteId',
};

const initialState: CaseNotePaneState = {
	pane: undefined,
	loading: false,
	lastError: '',
	profileId: 0,
	noteId: '',
	isPaneOpen: false,
	isEditing: false,
	canEditNote: false,
};

const store: Module<CaseNotePaneState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	mutations: {
		[SHOW_LOADING](state) {
			state.loading = true;
		},

		[HIDE_LOADING](state) {
			state.loading = false;
		},

		[SET_EDITING_MODE](state, bool) {
			state.isEditing = bool;
		},

		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},

		[TOGGLE_PANE](state, val?: CaseNotePaneKey) {
			state.pane = val;

			if (val) {
				//check if any pending close
				if (state.paneCloseTimeout) {
					clearTimeout(state.paneCloseTimeout);
					state.paneCloseTimeout = undefined;
				}
			}
		},

		[SET_PROFILE_ID](state, val?: number) {
			state.profileId = val;
		},

		[SET_NOTE_ID](state, val?: string) {
			state.noteId = val;
		},

		[SET_CAN_EDIT_NOTE](state, val: boolean) {
			state.canEditNote = val;
		},

		[SET_IS_PANE_OPEN](state, val: boolean) {
			state.isPaneOpen = val;
		},

		[SET_PANE_CLOSE_TIMEOUT](state, val?: number) {
			state.paneCloseTimeout = val;
		},
	},

	actions: {
		[CaseNotePaneActionKeys.openPane](
			{ commit },
			{
				pane,
				profileId,
				noteId,
				canEditNote,
			}: {
				pane: CaseNotePaneKey;
				profileId: number;
				noteId?: string;
				canEditNote: boolean;
			},
		) {
			commit(SET_IS_PANE_OPEN, true);
			commit(SET_PROFILE_ID, profileId);
			commit(SET_NOTE_ID, noteId);
			commit(SET_CAN_EDIT_NOTE, canEditNote);

			commit(TOGGLE_PANE, pane);
		},
		[CaseNotePaneActionKeys.closePane]({ commit, state }) {
			commit(SET_IS_PANE_OPEN, false);
			commit(SET_EDITING_MODE, false);
			commit(SET_PROFILE_ID, 0);
			commit(SET_NOTE_ID, '');
			commit(SET_CAN_EDIT_NOTE, false);

			// delay 300ms so that application details content still display
			// when closing pane to smooth animation
			if (!state.paneCloseTimeout) {
				const paneCloseTimeout = setTimeout(
					() => commit(TOGGLE_PANE, undefined),
					300,
				);
				commit(SET_PANE_CLOSE_TIMEOUT, paneCloseTimeout);
			}
		},

		[CaseNotePaneActionKeys.setEditing]({ commit }) {
			commit(SET_EDITING_MODE, true);
		},
		[CaseNotePaneActionKeys.unsetEditing]({ commit }) {
			commit(SET_EDITING_MODE, false);
		},
	},

	getters: {
		[CaseNotePaneGetterKeys.loading]: state => state.loading,
		[CaseNotePaneGetterKeys.isEditing]: state => state.isEditing,
		[CaseNotePaneGetterKeys.lastError]: state => state.lastError,
		[CaseNotePaneGetterKeys.pane]: state => state.pane,
		[CaseNotePaneGetterKeys.isPaneOpen]: state => state.isPaneOpen,
		[CaseNotePaneGetterKeys.profileId]: state => state.profileId,
		[CaseNotePaneGetterKeys.noteId]: state => state.noteId,
		[CaseNotePaneGetterKeys.canEditNote]: state => state.canEditNote,
	},
};

export default store;
